/**
 * Üst menü çubuğu: kullanıcıya özel bölümler (Dashboard'lar, AI Raporları, Favoriler) ve
 * kök modüller başlığın ortasında yatay; klasörler ve bölümler açılır menü.
 *
 * Bölümler sol menüdekiyle aynı (navGroups): üst menüde yalnız modül ağacı vardı ve
 * "Üstte"yi seçen kullanıcı dashboard'larına ulaşamıyordu.
 *
 * Başlıkta logo, firma adı, kur ve araç düğmeleriyle aynı satırı paylaşıyor; ayrı bir
 * satır açmak dikey yer harcardı (eski 1024×768 monitörler). Sığmazsa kademeli daralır
 * (bkz. useTopNavFit): önce bütün öğeler yalnız simge (ad ipucunda), o da sığmazsa
 * sığanlar simge, kalanı "…" menüsünde. Kullanıcı "yalnız simge"yi başlıktaki düğmeyle
 * kalıcı da seçebilir (sol menüdeki daraltma gibi).
 *
 * Eskiden sığmayanlar yatay kaydırmaya düşüyordu — sağda kalan modüller görünmüyor,
 * kaydırma çubuğu da olmadığı için varlıkları fark edilmiyordu.
 *
 * Klasörlerin içi dar şeritteki gibi YANA açılır (#480): aşağı açılan ağaç, panelin
 * yüksekliğini aşıp kaydırma istiyordu ve alt klasörü açmak için önce tıklamak gerekiyordu.
 */
import { useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MoreHorizontal } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui/Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import type { UserScreenNode } from '@/shared/types/system';
import { useMenu, isLeaf } from './useMenu';
import { NavIcon } from './NavIcon';
import { FlyoutItems, holds } from './FlyoutItems';
import { useTopNavFit } from './useTopNavFit';
import { NavGroupItems, NavGroupSub, useNavGroups, type NavGroup } from './navGroups';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useTabsStore } from '../tabs/tabsStore';

const ITEM =
  'hover:bg-bg-hover focus-visible:ring-ring/40 flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none';
/** Simge düğmesi: genişliği sabit (ölçülmüyor) — ICON_W ile aynı olmalı. */
const ICON_ITEM =
  'hover:bg-bg-hover focus-visible:ring-ring/40 flex h-8 w-8 shrink-0 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none';
const ICON_W = 32;
const OVERFLOW_W = 56;
const GAP = 4;
const ACTIVE = 'bg-brand-500/10 text-brand-700 dark:text-brand-400 font-medium';
const OPEN = 'data-[state=open]:bg-bg-hover';
const MENU = 'max-h-[80vh] w-64 overflow-y-auto';

type Entry =
  { kind: 'group'; key: string; group: NavGroup } | { kind: 'node'; key: string; node: UserScreenNode };

export function TopNavBar() {
  const { t } = useTranslation();
  const { tree } = useMenu();
  const groups = useNavGroups();
  const openScreen = useOpenScreen();
  const compact = usePrefsStore((s) => s.prefs.topNavCompact);
  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const activeScreenId = activeTab?.screenId ?? null;

  // Bölümler önce (sol menüdeki sırayla), sonra modüller.
  const entries: Entry[] = [
    ...groups.map((g): Entry => ({ kind: 'group', key: `group:${g.id}`, group: g })),
    ...tree.map((n): Entry => ({ kind: 'node', key: n.screenId, node: n })),
  ];

  const barRef = useRef<HTMLElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const fit = useTopNavFit(barRef, rulerRef, entries.length, {
    iconWidth: ICON_W,
    overflowWidth: OVERFLOW_W,
    gap: GAP,
    compact,
  });
  const icons = fit.mode === 'icons';
  const visible = entries.slice(0, fit.visible);
  const overflow = entries.slice(fit.visible);
  const overflowGroups = overflow.filter((e) => e.kind === 'group').map((e) => e.group);
  const overflowNodes = overflow.filter((e) => e.kind === 'node').map((e) => e.node);

  const isActive = (e: Entry) =>
    e.kind === 'group' ? e.group.active : e.node.screenId === activeScreenId || holds(e.node, activeScreenId);

  /** Çubuktaki düğme: adlı ya da yalnız simge; klasör/bölüm için açılır menü tetikleyicisi. */
  const button = (e: Entry, props: { onClick?: () => void; menu?: boolean }) => {
    const caption = e.kind === 'group' ? e.group.caption : e.node.caption;
    const icon =
      e.kind === 'group' ? (
        <e.group.Icon className={icons ? 'h-5 w-5' : 'h-4 w-4 shrink-0'} />
      ) : (
        <NavIcon node={e.node} className={icons ? 'h-5 w-5' : 'h-4 w-4 shrink-0'} />
      );
    const current = e.kind === 'node' && e.node.screenId === activeScreenId ? ('page' as const) : undefined;
    if (icons) {
      return (
        <button
          type="button"
          onClick={props.onClick}
          aria-label={caption}
          aria-current={current}
          className={cn(ICON_ITEM, props.menu && OPEN, isActive(e) && ACTIVE)}
        >
          {icon}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={props.onClick}
        aria-current={current}
        className={cn(ITEM, props.menu && OPEN, isActive(e) && ACTIVE)}
      >
        {icon}
        {caption}
        {props.menu ? <ChevronDown className="text-fg-subtle h-3.5 w-3.5" /> : null}
      </button>
    );
  };

  /* Simge modunda ad ipucunda. İpucu tetikleyicinin DIŞINDA sarmalanır: Tooltip bileşeni
     aldığı özellikleri düğmeye geçirmiyor, içeride kalsa menü tetikleyicisi çalışmazdı. */
  const tip = (caption: string, el: ReactElement) => (icons ? <Tooltip content={caption}>{el}</Tooltip> : el);

  const render = (e: Entry) => {
    const caption = e.kind === 'group' ? e.group.caption : e.node.caption;
    if (e.kind === 'node' && isLeaf(e.node)) {
      const n = e.node;
      return (
        <span key={e.key} className="contents">
          {tip(caption, button(e, { onClick: () => openScreen(n) }))}
        </span>
      );
    }
    return (
      <DropdownMenu key={e.key}>
        {tip(caption, <DropdownMenuTrigger asChild>{button(e, { menu: true })}</DropdownMenuTrigger>)}
        <DropdownMenuContent align="start" className={MENU}>
          {/* Simge modunda ad menünün başlığında: düğmede yazmıyor. */}
          {icons ? <DropdownMenuLabel>{caption}</DropdownMenuLabel> : null}
          {e.kind === 'group' ? (
            <NavGroupItems group={e.group} />
          ) : (
            <FlyoutItems nodes={e.node.children} activeScreenId={activeScreenId} onOpen={openScreen} />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <nav
      ref={barRef}
      aria-label={t('shell.menu')}
      className="relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
    >
      {/* Cetvel: bütün öğeler adıyla, görünmez; sığdırma hesabı buradan ölçülür. */}
      <div
        ref={rulerRef}
        aria-hidden="true"
        data-testid="topnav-ruler"
        className="pointer-events-none invisible absolute top-0 left-0 flex w-max items-center gap-1"
      >
        {entries.map((e) => (
          <span key={e.key} className={ITEM}>
            {e.kind === 'group' ? (
              <e.group.Icon className="h-4 w-4 shrink-0" />
            ) : (
              <NavIcon node={e.node} className="h-4 w-4 shrink-0" />
            )}
            {e.kind === 'group' ? e.group.caption : e.node.caption}
            {e.kind === 'group' || !isLeaf(e.node) ? <ChevronDown className="h-3.5 w-3.5" /> : null}
          </span>
        ))}
      </div>
      {visible.map(render)}
      {overflow.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('shell.moreModules')}
              title={t('shell.moreModules')}
              className={cn(ITEM, OPEN, overflow.some(isActive) && ACTIVE)}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="text-fg-subtle tabular-nums">{overflow.length}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={MENU}>
            <DropdownMenuLabel>{t('shell.moreModules')}</DropdownMenuLabel>
            {overflowGroups.map((g) => (
              <NavGroupSub key={g.id} group={g} />
            ))}
            {overflowGroups.length > 0 && overflowNodes.length > 0 ? <DropdownMenuSeparator /> : null}
            {/* Taşan kökler kendi alt ağaçlarıyla birlikte iner: menüde kaybolmasınlar. */}
            <FlyoutItems nodes={overflowNodes} activeScreenId={activeScreenId} onOpen={openScreen} />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </nav>
  );
}
